import * as THREE from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { settings } from '@/config';
import { autoSmoothNormals } from '@/utils/autoSmooth';
import { validateModel } from '@/utils/ModelValidator';
import type { MaterialManager } from '@/materials/MaterialManager';
import type { ModelValidationReport, TickState } from '@/types';

/**
 * Wraps a loaded GLTF: enables shadows, routes every mesh through the
 * MaterialManager (UV remap + resolver + normalization), optionally
 * centers/normalizes transforms, sets up an AnimationMixer, and validates the
 * result. The GLB can be swapped with zero code changes — only the manifest.
 */
export class Model {
  readonly root: THREE.Object3D;
  readonly animations: THREE.AnimationClip[];
  readonly cameras: THREE.Camera[];
  readonly mixer: THREE.AnimationMixer | null;
  readonly report: ModelValidationReport;

  /** When false, the mixer is driven externally (e.g. scroll-scrubbed baked camera). */
  autoUpdateMixer = true;

  constructor(gltf: GLTF, materials: MaterialManager) {
    this.root = gltf.scene;
    this.animations = gltf.animations ?? [];
    this.cameras = (gltf.cameras ?? []) as THREE.Camera[];

    this.sanitizeNames(gltf);
    this.process(materials);
    if (settings.world.model.autoCenter || settings.world.model.normalizeScale) {
      this.normalizeTransform();
    }
    this.mixer = this.animations.length ? new THREE.AnimationMixer(this.root) : null;
    this.report = validateModel(this.root, this.animations, this.cameras);
  }

  /**
   * Strip Blender's auto-appended duplicate-name suffix (`Stage_Pillar.002`, `5_E.002`,
   * `1.003`, ...) so every downstream name-based lookup — the material resolver, the arch
   * voussoir/collider collection in `World`, the emissive-twin lookup in
   * `StoneInteraction` — keeps working against the clean names (`Stage_Pillar`, `5_E`,
   * `1`) it was written for. The suffix appears whenever Blender merges/appends objects
   * whose names collide with ones already in the file; it is not semantic.
   *
   * GOTCHA #1: `THREE.Object3D.name` at this point is already `PropertyBinding.sanitizeNodeName`'d
   * by GLTFLoader, which DELETES the `.` (a reserved animation-path character) rather than
   * keeping it — `"5_E.002"` arrives as `"5_E002"`, indistinguishable by regex from a
   * hypothetical real name. So read the un-mangled name back out of the raw glTF JSON via
   * `parser.associations` (Object3D -> {nodes: nodeIndex}) instead of trusting `.name`.
   *
   * GOTCHA #2 (2026-07-04, a real regression this caused): GLTFLoader bakes each
   * `AnimationClip`'s `KeyframeTrack.name` as `"<mangledNodeName>.<property>"` — e.g.
   * `"Camera003.position"` — using the node's `.name` AT PARSE TIME. Renaming the node
   * afterward (as above) without ALSO fixing up the track names breaks every binding:
   * `PropertyBinding` looks for an object literally named `"Camera003"`, finds none
   * (it's now `"Camera"`), logs "No target node found", and silently no-ops — the
   * mixer runs but drives nothing. This is exactly what made the baked camera (and
   * every other baked animation) stop responding to scroll. So: repair every track's
   * node-name prefix to match the rename, right here, using the same old→new map.
   */
  private sanitizeNames(gltf: GLTF): void {
    const parser = gltf.parser;
    const nodes = parser?.json?.nodes as Array<{ name?: string }> | undefined;
    if (!parser || !nodes) return;

    // Pass 1: stamp the ORIGINAL Blender name (suffix intact) on every node so downstream
    // lookups can disambiguate nodes whose clean names collide (e.g. `Stage_S_4` vs
    // `Stage_S_4.001`, different baked atlases), and tally how many nodes each CLEANED
    // name would collapse to.
    const cleanCount = new Map<string, number>();
    const rawByChild = new Map<THREE.Object3D, string>();
    gltf.scene.traverse((child) => {
      const nodeIndex = parser.associations.get(child)?.nodes;
      const rawName = nodeIndex !== undefined ? nodes[nodeIndex]?.name : undefined;
      if (!rawName) return;
      child.userData.rawName = rawName;
      rawByChild.set(child, rawName);
      const clean = rawName.replace(/\.\d+$/, '');
      cleanCount.set(clean, (cleanCount.get(clean) ?? 0) + 1);
    });

    // Pass 1b: propagate `rawName` down to descendants that lack one. A glTF MESH with MORE
    // THAN ONE primitive is loaded as a GROUP whose child primitive Meshes carry a `{meshes}`
    // association, NOT `{nodes}` — so pass 1 (which keys off `.nodes`) never stamped them. The
    // material resolver reads `userData.rawName`, so those unstamped children couldn't be
    // identified and fell through to the neutral FALLBACK — meshes like `Stage_S_4`,
    // `Stage_S_4.001`, `Stage__S_3`, `Left_Door`, `Right_Door` (all 2-primitive) rendered
    // untextured. Inheriting the parent node's rawName fixes every name-based lookup for them.
    gltf.scene.traverse((child) => {
      if (child.userData.rawName) return;
      let ancestor = child.parent;
      while (ancestor && !ancestor.userData.rawName) ancestor = ancestor.parent;
      if (ancestor?.userData.rawName) child.userData.rawName = ancestor.userData.rawName;
    });

    // Pass 2: strip the `.NNN` suffix ONLY when the clean name stays UNIQUE. Renaming a
    // colliding group (e.g. the camera-rig empties `Empty.003`/`Empty.004`/`Empty.005`,
    // which all clean to `Empty`) would fold them onto ONE name — then every animation
    // track bound to the others silently retargets the first `Empty` in traversal order
    // and stops driving its node. That is exactly what froze the baked camera: it is
    // parented to `Empty.003`, whose path clip (`EmptyAction.002`) was mis-binding.
    // Colliding nodes keep GLTFLoader's already-unique sanitized names (`Empty003`…), so
    // their baked tracks keep resolving untouched.
    const renames = new Map<string, string>(); // old (mangled) name -> new (clean) name
    gltf.scene.traverse((child) => {
      const rawName = rawByChild.get(child);
      if (!rawName) return;
      const clean = rawName.replace(/\.\d+$/, '');
      if (clean !== child.name && cleanCount.get(clean) === 1) {
        renames.set(child.name, clean);
        child.name = clean;
      }
    });
    if (renames.size === 0) return;

    for (const clip of this.animations) {
      for (const track of clip.tracks) {
        const dot = track.name.indexOf('.');
        if (dot === -1) continue;
        const newName = renames.get(track.name.slice(0, dot));
        if (newName) track.name = newName + track.name.slice(dot);
      }
    }
  }

  private process(materials: MaterialManager): void {
    const cfg = settings.world.model;
    // Geometries can be shared between meshes — smooth each one exactly once.
    const smoothed = new Set<THREE.BufferGeometry>();
    this.root.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = cfg.castShadow;
      mesh.receiveShadow = cfg.receiveShadow;
      if (cfg.autoSmooth.enabled && mesh.geometry && !smoothed.has(mesh.geometry)) {
        smoothed.add(mesh.geometry);
        autoSmoothNormals(mesh.geometry, cfg.autoSmooth.angleDeg);
      }
      materials.applyToMesh(mesh);
    });
  }

  private normalizeTransform(): void {
    const cfg = settings.world.model;
    const box = new THREE.Box3().setFromObject(this.root);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    if (cfg.autoCenter) this.root.position.sub(center);
    if (cfg.normalizeScale) {
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      this.root.scale.multiplyScalar(cfg.targetSize / maxDim);
    }
  }

  /** Play every baked clip (used so `mixer.setTime` can scrub them). */
  playAll(): void {
    for (const clip of this.animations) this.mixer?.clipAction(clip).play();
  }

  getBounds(): THREE.Box3 {
    return new THREE.Box3().setFromObject(this.root);
  }

  update(tick: TickState): void {
    if (this.autoUpdateMixer) this.mixer?.update(tick.delta);
  }
}
